import React from "react";
import Modal from "../common/Modal";
import type { AuthoritySop } from "../../services/governanceService";

// ── Shared chip/badge meta (also imported by AuthorityDetailModal) ──────────
// Maps an SOP step role to a human label + a status-badge variant suffix.
export const ROLE_META: Record<string, { label: string; badge: string }> = {
  PERFORM: { label: "Performs", badge: "info" },
  APPROVE: { label: "Approves", badge: "success" },
  REVIEW:  { label: "Reviews", badge: "warning" },
  NOTIFY:  { label: "Notified", badge: "neutral" },
  OTHER:   { label: "Other", badge: "neutral" },
};

export const roleMeta = (role: string): { label: string; badge: string } =>
  ROLE_META[role] ?? { label: role, badge: "neutral" };

/** SOP publish-status → status-badge variant suffix. */
export const sopStatusBadge = (status: string): string =>
  status === "APPROVED" ? "success" : status === "DRAFT" ? "warning" : "neutral";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sop: AuthoritySop | null;
  authorityName: string;
}

/**
 * Small nested modal opened from the SOPs tab of AuthorityDetailModal. Pure
 * presentation of the already-loaded `engagements` — no fetch. Shows exactly
 * which step(s) + in what capacity (role) the authority is a stakeholder in
 * this one SOP. Stacks above the parent detail modal (both portal to body; this
 * one mounts later, so it paints on top at the shared overlay z-index).
 */
const AuthoritySopInvolvementModal: React.FC<Props> = ({ isOpen, onClose, sop, authorityName }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" closeOnOverlayClick closeOnEscape>
      <Modal.Header>
        In "<span className="item-highlight">{sop?.process_name ?? ""}</span>"
      </Modal.Header>
      <Modal.Body>
        <div data-testid="MOD_authority_sop_involvement">
          <p className="text-xs text-gray-500 mb-3">
            How <strong className="text-gray-700">{authorityName}</strong> is involved:
          </p>
          {sop && sop.engagements.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {sop.engagements.map((e) => {
                const rm = roleMeta(e.role);
                return (
                  <li
                    key={`${e.step_id}-${e.role}`}
                    data-testid={`ROW_authority_sop_engagement_${e.step_id}_${e.role}`}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2"
                  >
                    <span className="flex-shrink-0 text-[11px] font-semibold text-gray-400 w-12">
                      Step {e.step_number}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-sm text-gray-800">{e.step_name}</span>
                    <span className={`status-badge xs ${rm.badge}`}>{rm.label}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 py-4 text-center">No step engagements found.</p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AuthoritySopInvolvementModal;
